// Kontext obrazovky pro panel AI asistenta (docs/11-ai-asistent.md §2 bod 5, §9 bod 4;
// docs/09-technologie.md §11). Každá obrazovka řekne, kde uživatel je — sekce, otevřená entita
// a její id, výběr v seznamu, aktivní filtr — aby požadavek „vystav k tomu fakturu“ fungoval bez
// vysvětlování. Panel asistenta (krok 3.8) si aktuální kontext přečte přes useAktualniKontextObrazovky.
//
// Použití na obrazovce:
//   useKontextObrazovky({ sekce: 'zakaznici', entita: { typ: 'zakaznik', id, popis: zakaznik.nazev } });
//
// Globální úložiště bez providera (useSyncExternalStore), aby obrazovky nemusely nic obalovat.
// Při odchodu z obrazovky se její kontext sám odebere; platí **nejpozději vykreslená** obrazovka: pořadí
// určuje první render (rodič před dítětem, stránka před kartou a oknem nad ní, později otevřené okno až
// nakonec), ne to, kdy obrazovce dorazí data. Kdyby rozhodovalo pořadí efektů, stránka, jejíž data dorazila
// ve stejném commitu jako data karty, by kartu přeskočila — React spouští efekty dětí před rodiči
// (Nastavení › Firma vs. karta napojení na banku, 21. 9. 2026). Změna kontextu (dojdou data, filtr) proto
// pozici nemění.
import { useEffect, useRef, useSyncExternalStore } from 'react';

export interface EntitaKontextu {
  /** Typ entity podle názvu tabulky (`zakaznik`, `zakazka`, `zamestnanec`…). */
  typ: string;
  id: string;
  /** Lidský popis pro asistenta a pro zobrazení v panelu („Harimatec s.r.o.“). */
  popis?: string;
}

export interface KontextObrazovky {
  /** Klíč sekce z 08 §2 (`zakaznici`, `nastaveni`…). */
  sekce: string;
  /** Název obrazovky nebo záložky, jak ho vidí uživatel. */
  obrazovka?: string;
  entita?: EntitaKontextu;
  /** Vybrané řádky seznamu. */
  vyber?: { typ: string; ids: string[] };
  /** Aktivní filtr a hledání seznamu (hodnoty, jak je posílá API). */
  filtr?: Record<string, unknown>;
  /**
   * Co obrazovka právě ukazuje v číslech ze serveru (souhrn bloku, stav běhu) — např. Pohledávky
   * `{ profit_rucne: souhrn }`. Jen hotová data z API, nic dopočteného v UI.
   */
  udaje?: Record<string, unknown>;
}

interface Zaznam {
  klic: number;
  kontext: KontextObrazovky;
}

let zasobnik: Zaznam[] = [];
let dalsiKlic = 1;
const posluchaci = new Set<() => void>();

function oznamit() {
  posluchaci.forEach((posluchac) => posluchac());
}

function prihlasit(posluchac: () => void) {
  posluchaci.add(posluchac);
  return () => {
    posluchaci.delete(posluchac);
  };
}

function aktualni(): KontextObrazovky | null {
  return zasobnik.length > 0 ? zasobnik[zasobnik.length - 1].kontext : null;
}

function odebrat(klic: number) {
  if (!zasobnik.some((zaznam) => zaznam.klic === klic)) return;
  zasobnik = zasobnik.filter((zaznam) => zaznam.klic !== klic);
  oznamit();
}

/** Zapíše kontext obrazovky; zásobník drží pořadí podle klíče (pořadí prvního renderu), vrchol = nejvyšší klíč. */
function zapsat(klic: number, kontext: KontextObrazovky) {
  const zaznam = { klic, kontext };
  zasobnik = [...zasobnik.filter((z) => z.klic !== klic), zaznam].sort((a, b) => a.klic - b.klic);
  oznamit();
}

/**
 * Obrazovka publikuje svůj kontext. Zápis probíhá v efektu (ne při renderu), takže je bezpečný
 * i ve StrictMode; při změně hodnot se kontext nahradí na svém místě, při odchodu z obrazovky odebere.
 * `null` = obrazovka teď nic nepublikuje (kontext zmizí, dokud zase nemá co říct).
 */
export function useKontextObrazovky(kontext: KontextObrazovky | null): void {
  const serializovany = kontext ? JSON.stringify(kontext) : null;
  // Klíč = pořadí prvního renderu téhle obrazovky. Přidělení při renderu je záměr (viz hlavička): jde jen
  // o pořadí, přeskočené číslo z dvojího renderu ve StrictMode nevadí a nic dalšího se při renderu neděje.
  const klicRef = useRef<number | null>(null);
  if (klicRef.current === null) {
    klicRef.current = dalsiKlic;
    dalsiKlic += 1;
  }
  const klic = klicRef.current;
  // Odchod z obrazovky (a simulovaný odchod ve StrictMode) — kontext zmizí.
  useEffect(() => () => odebrat(klic), [klic]);
  useEffect(() => {
    if (!serializovany) {
      odebrat(klic);
      return;
    }
    zapsat(klic, JSON.parse(serializovany) as KontextObrazovky);
  }, [klic, serializovany]);
}

/** Aktuální kontext pro panel asistenta; null, když žádná obrazovka kontext nepublikuje. */
export function useAktualniKontextObrazovky(): KontextObrazovky | null {
  return useSyncExternalStore(prihlasit, aktualni, aktualni);
}

/** Pro testy: vyprázdní úložiště. */
export function vycistitKontextObrazovky() {
  zasobnik = [];
  oznamit();
}
